import type { AiMatch, Itinerary } from "../types";
import type { Trip, TripDay, TripStop, TripStyle } from "../types";
import { generateId } from "./id";

function daysToTripDays(
  itinerary: Itinerary,
  matches: AiMatch[],
  labelOffset: number,
): TripDay[] {
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  return itinerary.days.map((day, i) => {
    const stops: TripStop[] = [];
    for (const slot of day.slots) {
      const m = slot.matchSlug ? bySlug.get(slot.matchSlug) : undefined;
      if (m && m.id) stops.push({ id: generateId("stop"), itemId: m.id });
    }
    return { id: generateId("day"), label: `Day ${labelOffset + i + 1}`, stops };
  });
}

export function deriveTripName(itinerary: Itinerary, fallbackName: string): string {
  if (itinerary.cities && itinerary.cities.length > 0) {
    return itinerary.cities.join(" & ");
  }
  const firstCity = itinerary.days.find((d) => d.city)?.city;
  return firstCity ?? fallbackName;
}

function firstCitySlug(itinerary: Itinerary, matches: AiMatch[]): string {
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  for (const day of itinerary.days) {
    for (const slot of day.slots) {
      const m = slot.matchSlug ? bySlug.get(slot.matchSlug) : undefined;
      if (m?.citySlug) return m.citySlug;
    }
  }
  return "";
}

export function buildTripFromItinerary(
  itinerary: Itinerary,
  matches: AiMatch[],
  fallbackName: string,
  opts?: { name?: string; style?: TripStyle; startDate?: string },
): Trip {
  return {
    id: generateId("trip"),
    name: opts?.name ?? deriveTripName(itinerary, fallbackName),
    city: firstCitySlug(itinerary, matches),
    style: opts?.style ?? "culture",
    createdAt: new Date().toISOString(),
    // The traveller's real first day ("2026-11-06"), when they gave dates.
    // Left out otherwise; trip-storage then dates the trip from createdAt.
    ...(opts?.startDate ? { startDate: opts.startDate } : {}),
    days: daysToTripDays(itinerary, matches, 0),
  };
}

export function mergeItineraryIntoTrip(
  activeTrip: Trip,
  itinerary: Itinerary,
  matches: AiMatch[],
): Trip {
  const appended = daysToTripDays(itinerary, matches, activeTrip.days.length);
  return { ...activeTrip, days: [...activeTrip.days, ...appended] };
}

/**
 * How many of the trip's own days an AI plan can actually fill.
 *
 * The button says this number, so it has to be the number the fill produces:
 * a 2-day plan on a 3-day trip fills 2, a 5-day plan on a 3-day trip fills 3.
 */
export function fillableDayCount(trip: Trip, itinerary: Itinerary): number {
  return Math.min(trip.days.length, itinerary.days.length);
}

/**
 * Lay an AI plan over the days the traveller already made.
 *
 * `mergeItineraryIntoTrip` appends — right when a chat itinerary arrives with
 * no trip to land in, wrong here. Someone who built a 3-day Marrakech trip and
 * asked the concierge to plan it wants those three days filled, not three empty
 * days followed by the AI's two. Day count never changes; a plan shorter than
 * the trip leaves the rest blank (the empty-day suggestions already speak
 * there) and a plan longer than the trip stops at the last day the traveller
 * asked for.
 *
 * Existing stops are kept and the plan lands after them, so this never eats
 * work already on the day. Anything already somewhere in the trip is skipped —
 * re-adding a place you have is noise, the same rule the vibe suggestions use.
 */
export function fillTripDaysFromItinerary(
  trip: Trip,
  itinerary: Itinerary,
  matches: AiMatch[],
): Trip {
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  const taken = new Set(trip.days.flatMap((d) => d.stops.map((s) => s.itemId)));

  const days = trip.days.map((day, i) => {
    const planned = itinerary.days[i];
    if (!planned) return day;

    const added: TripStop[] = [];
    for (const slot of planned.slots) {
      const m = slot.matchSlug ? bySlug.get(slot.matchSlug) : undefined;
      if (!m?.id || taken.has(m.id)) continue;
      taken.add(m.id);
      added.push({ id: generateId("stop"), itemId: m.id });
    }
    return added.length === 0 ? day : { ...day, stops: [...day.stops, ...added] };
  });

  return { ...trip, days };
}

export function countStops(trip: Trip): number {
  return trip.days.reduce((n, d) => n + d.stops.length, 0);
}
